import router from '@/router';

interface PerfReplayOptions {
    /** Number of clicks to perform (default 8). Ignored when urls is provided. */
    count?: number;
    /** Wait time in ms after each navigation settles before the next click (default 0 = click as fast as possible). */
    delayMs?: number;
    /** Explicit category paths to click in order (e.g. ['/en-zz/a/b/c-123', '/en-zz/a/d/c-456']). */
    urls?: string[];
}

const CATEGORY_HREF_PATTERN = /\/c-\d+\/?$/;
const NAVIGATION_TIMEOUT_MS = 10000;

class PerfReplayService {
    private stopped = true;
    private pendingNavigation: (() => void) | null = null;
    private lastTarget: string | undefined;
    private usedTargets = new Set<string>();

    constructor() {
        router.afterEach(() => {
            const resolve = this.pendingNavigation;
            this.pendingNavigation = null;
            resolve?.();
        });
    }

    start(options: PerfReplayOptions = {}): string {
        if (!this.stopped) {
            console.warn('[Perf] replay: already running — call __perfReplay.stop() first');
            return 'already-running';
        }
        const count = options.count ?? 8;
        const delayMs = options.delayMs ?? 0;
        const urls = options.urls ?? [];
        this.stopped = false;
        this.lastTarget = undefined;
        this.usedTargets.clear();
        console.debug(`[Perf] replay: starting (clicks=${urls.length || count}, delayMs=${delayMs})`);
        void this.run(urls, count, delayMs);
        return 'started';
    }

    stop(): string {
        if (this.stopped) return 'not-running';
        this.stopped = true;
        console.debug('[Perf] replay: stopped by user');
        return 'stopped';
    }

    private async run(urls: string[], count: number, delayMs: number): Promise<void> {
        let step = 0;
        while (!this.stopped && step < count) {
            const target = urls[step] ?? this.pickNextCategoryPath();
            if (!target) {
                console.debug('[Perf] replay: no category links found on page — stopping');
                return;
            }

            const clickedAt = performance.now();
            console.debug(`[Perf] replay: click #${step + 1} -> ${target}`);
            await this.click(target);
            await this.waitForNavigation();
            if (this.stopped) return;

            console.debug(`[Perf] replay: click #${step + 1} navigated in ${Math.round(performance.now() - clickedAt)}ms | ${window.location.pathname}`);
            step++;

            if (delayMs > 0 && step < count) {
                await this.wait(delayMs);
            }
        }

        if (!this.stopped) {
            console.debug(`[Perf] replay: finished (${step} clicks)`);
        }
        this.stopped = true;
    }

    private getCategoryAnchors(): HTMLAnchorElement[] {
        const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'));
        const seen = new Set<string>();
        const result: HTMLAnchorElement[] = [];
        for (const anchor of anchors) {
            const path = anchor.pathname;
            if (!CATEGORY_HREF_PATTERN.test(path)) continue;
            if (path === window.location.pathname) continue;
            if (seen.has(path)) continue;
            seen.add(path);
            result.push(anchor);
        }
        return result;
    }

    private pickNextCategoryPath(): string | undefined {
        const anchors = this.getCategoryAnchors();
        if (!anchors.length) return undefined;

        // Prefer a target not yet visited; rotate through distinct categories
        // instead of ping-ponging between the first two links.
        const unvisited = anchors.find(a => !this.usedTargets.has(a.pathname));
        const anchor = unvisited ?? anchors[(this.usedTargets.size) % anchors.length];
        this.usedTargets.add(anchor.pathname);
        this.lastTarget = anchor.pathname;
        return this.lastTarget;
    }

    private async click(target: string): Promise<void> {
        const anchors = this.getCategoryAnchors();
        const anchor = anchors.find(a => a.pathname === target)
            ?? anchors.find(a => a.pathname.endsWith(target));
        if (anchor) {
            anchor.click();
            return;
        }
        await router.push(target);
    }

    private waitForNavigation(timeoutMs: number = NAVIGATION_TIMEOUT_MS): Promise<void> {
        return new Promise(resolve => {
            const timer = setTimeout(() => {
                this.pendingNavigation = null;
                console.warn(`[Perf] replay: navigation did not complete within ${timeoutMs}ms — continuing`);
                resolve();
            }, timeoutMs);
            this.pendingNavigation = () => {
                clearTimeout(timer);
                resolve();
            };
        });
    }

    private wait(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

const replay = new PerfReplayService();

export function initPerfReplay(): void {
    const fire = (options?: PerfReplayOptions) => replay.start(options);
    fire.stop = () => replay.stop();
    (window as unknown as Record<string, unknown>).__perfReplay = fire;
}
